import { World, Circle, Box, Vec2, type Body } from 'planck';
import {
  POOL_BALL_RADIUS,
  POOL_CUE_BALL,
  POOL_POCKETS,
  POOL_POCKET_RADIUS,
  POOL_TABLE_LENGTH,
  POOL_TABLE_WIDTH,
  quantize,
  sortBalls,
  type PoolBall,
  type PoolPoint,
  type PoolShotOutcome,
} from '@awwyeah/shared';

/**
 * The pool simulator (WS-17e) — the *only* file in the app that knows Planck exists.
 * Vendored via npm (`planck@1.5.0`, MIT, zero runtime dependencies of its own; north-star
 * #7 — bundled with the app, never fetched from anywhere). Lives in the web app rather than
 * `@awwyeah/shared` on purpose: the server's push notifier and the rules engine work off the
 * recorded outcome snapshot (see `packages/shared/src/modules/games/pool.ts`), so Node never
 * needs a physics engine and it stays out of the server bundle entirely.
 *
 * ## Fixed timestep, decoupled render — and why the loop is shaped like this
 *
 * The classic "fix your timestep" structure (LittleJS and every other engine documents the
 * same pattern) is: accumulate real time, run the simulation in fixed `dt` chunks, then
 * render *interpolated* between the last two simulated states so the picture is smooth even
 * though the physics is quantised. We keep both halves, but split them in time rather than
 * interleaving them in one loop:
 *
 *  - **`simulateShot` runs the entire shot to rest, up front**, in fixed `POOL_PHYSICS_DT`
 *    steps with fixed solver iteration counts, recording a position sample every
 *    `POOL_FRAME_STRIDE` steps. Nothing about it depends on wall-clock time, frame rate, a
 *    display refresh, or how busy the phone is.
 *  - **`PoolShotPlayback` (playback.ts) is the render half**: `requestAnimationFrame` maps
 *    elapsed wall-clock time onto that sample buffer and interpolates linearly between the
 *    two straddling samples.
 *
 * Interleaving would have been the wrong shape here. This is a turn-based game: we need the
 * *outcome* the instant the player releases their finger (to write it into the move log and
 * hand the turn over) and we need the identical outcome on a device that is only replaying
 * a shot somebody else took. Simulating up-front makes both of those the same code path, and
 * it makes the physics completely independent of rendering — a backgrounded tab, a 120 Hz
 * iPad and a throttled old phone all produce bit-identical results, because none of them
 * ever feed a real elapsed time into the solver.
 *
 * ## The determinism contract
 *
 * Same Planck version + same fixed `dt` + same iteration counts + same canonical rack ⇒
 * same result. Concretely, what this file must never do (and doesn't): never step with a
 * variable `dt`, never mutate Planck's global `Settings` (a shared static — one mutation
 * would silently change every past game's replay), never iterate bodies in an order that
 * depends on anything but creation order, never let a random number or a clock reading
 * reach the solver.
 *
 * One honest caveat, and the reason the snapshot-trust guard exists at all: Planck's solver
 * is IEEE-754 double arithmetic (`+ - * /`, `Math.sqrt`, `Math.abs`, `Math.min/max` — all
 * exactly specified, so identical on every engine), *but* body rotation goes through
 * `Math.sin`/`Math.cos`, which are **not** exactly specified and may differ by an ulp
 * between V8, JavaScriptCore and SpiderMonkey. We believe that can't affect positions here:
 * every ball fixture is a circle centred on its body origin, and a circle centred on the
 * origin is rotation-invariant — the transform's rotation multiplies a zero vector, so the
 * sin/cos results only ever reach the *rendered* spin, never the collision geometry.
 * "We believe" is doing real work in that sentence, which is exactly why the shooter's
 * snapshot is authoritative and divergences get logged instead of trusted.
 */

/** 120 Hz. Small enough that a hard break doesn't need continuous collision to look right,
 * large enough that a long shot is a few hundred steps, not a few thousand. */
export const POOL_PHYSICS_DT = 1 / 120;
export const POOL_VELOCITY_ITERATIONS = 8;
export const POOL_POSITION_ITERATIONS = 3;

/** Speed at `power = 1`, in table units per second (the table is 4 long). */
export const POOL_MAX_SHOT_SPEED = 9;
/** Even the gentlest tap moves the ball — a shot that does nothing would just be a
 * confusing way to lose your turn. */
export const POOL_MIN_SHOT_SPEED = 0.9;

/** Below this, a ball is "stopped" — velocities are zeroed rather than left to decay
 * asymptotically, which is what makes the resting position crisp and reproducible. */
const STOP_SPEED = 0.06;
/** Hard ceiling so a pathological shot can't hang the UI. 12 s of simulated time. */
const MAX_STEPS = Math.round(12 / POOL_PHYSICS_DT);
/** Record a position sample every other step → a 60 Hz sample buffer for playback. */
export const POOL_FRAME_STRIDE = 2;

const BALL_DENSITY = 1;
const BALL_RESTITUTION = 0.94;
const BALL_FRICTION = 0.2;
const BALL_LINEAR_DAMPING = 1;
const BALL_ANGULAR_DAMPING = 1.5;
const CUSHION_RESTITUTION = 0.6;
const CUSHION_FRICTION = 0.2;
const CUSHION_THICKNESS = 0.2;

/** One sampled instant of a shot: every ball still on the table. */
export type PoolFrame = PoolBall[];

/**
 * Something audible happened — recorded for the sound effects and nothing else. Collected
 * by *listening* to Planck's contact callbacks (reading velocities, never writing them) and
 * by the pocket sweep, so recording them cannot change a single number in the outcome.
 */
export interface PoolSoundEvent {
  /** Simulated milliseconds from the strike. */
  atMs: number;
  kind: 'ball' | 'cushion' | 'pocket';
  /** Closing speed in table units/second (0 for a pocket) — loudness. */
  speed: number;
}

export interface PoolSimulation {
  outcome: PoolShotOutcome;
  /** Clicks, cushion thuds and drops, in time order. Display-only. */
  events: PoolSoundEvent[];
  /** Position samples at `POOL_PHYSICS_DT * POOL_FRAME_STRIDE` intervals, first = the
   * layout the shot started from, last = everything at rest. */
  frames: PoolFrame[];
  /** How long the shot actually took, in simulated milliseconds. */
  durationMs: number;
}

export interface PoolShotInput {
  angle: number;
  power: number;
}

/**
 * Builds the cushions: each rail is split into straight segments with a gap at every pocket,
 * so a ball that finds a pocket mouth physically leaves the playing surface instead of
 * bouncing off a wall that a "pocket" sensor then has to argue with. Segments are thick
 * boxes (not thin edges) sitting *outside* the playing rectangle — thickness plus bullet
 * bodies means nothing tunnels, even off a full-power break.
 */
function createCushions(world: World): void {
  const mouth = POOL_POCKET_RADIUS;
  const L = POOL_TABLE_LENGTH;
  const W = POOL_TABLE_WIDTH;
  const half = CUSHION_THICKNESS / 2;

  const add = (cx: number, cy: number, hx: number, hy: number): void => {
    if (hx <= 0 || hy <= 0) return;
    const body = world.createBody({ type: 'static', position: Vec2(cx, cy) });
    body.createFixture(Box(hx, hy), {
      density: 0,
      friction: CUSHION_FRICTION,
      restitution: CUSHION_RESTITUTION,
    });
  };

  // Long rails (y = 0 and y = W), each broken by a corner pocket at both ends and a side
  // pocket in the middle.
  const longSpans: [number, number][] = [
    [mouth, L / 2 - mouth],
    [L / 2 + mouth, L - mouth],
  ];
  for (const [x1, x2] of longSpans) {
    add((x1 + x2) / 2, -half, (x2 - x1) / 2, half);
    add((x1 + x2) / 2, W + half, (x2 - x1) / 2, half);
  }
  // Short rails (x = 0 and x = L), broken only by the two corner pockets.
  const shortSpan: [number, number] = [mouth, W - mouth];
  add(-half, (shortSpan[0] + shortSpan[1]) / 2, half, (shortSpan[1] - shortSpan[0]) / 2);
  add(L + half, (shortSpan[0] + shortSpan[1]) / 2, half, (shortSpan[1] - shortSpan[0]) / 2);
}

function createBall(world: World, ball: PoolBall): Body {
  const body = world.createBody({
    type: 'dynamic',
    position: Vec2(ball.x, ball.y),
    linearDamping: BALL_LINEAR_DAMPING,
    angularDamping: BALL_ANGULAR_DAMPING,
    bullet: true,
    userData: ball.n,
  });
  body.createFixture(Circle(POOL_BALL_RADIUS), {
    density: BALL_DENSITY,
    friction: BALL_FRICTION,
    restitution: BALL_RESTITUTION,
  });
  return body;
}

/** A ball is pocketed when its centre reaches a pocket's mouth — checked between steps, so
 * it never races the solver. */
function pocketHit(x: number, y: number): boolean {
  return POOL_POCKETS.some((p) => Math.hypot(p.x - x, p.y - y) < POOL_POCKET_RADIUS);
}

/** Belt and braces: if a ball somehow leaves the table without being caught by a pocket
 * (it shouldn't be possible with bullet bodies and 0.2-thick cushions), treat it as
 * pocketed rather than let it fly off into nothing. */
function escaped(x: number, y: number): boolean {
  const m = POOL_BALL_RADIUS * 2;
  return x < -m || y < -m || x > POOL_TABLE_LENGTH + m || y > POOL_TABLE_WIDTH + m;
}

function snapshotBodies(bodies: Map<number, Body>): PoolFrame {
  const out: PoolBall[] = [];
  for (const [n, body] of bodies) {
    const p = body.getPosition();
    out.push({ n, x: p.x, y: p.y });
  }
  return sortBalls(out);
}

/**
 * Runs one shot from `startBalls` to rest and returns both the rules-facing outcome
 * snapshot and the sample buffer the animation plays back. Pure with respect to its inputs:
 * a fresh `World` every call, no module state, no clock, no randomness — call it twice with
 * the same arguments and you get the same numbers, which is the property
 * `apps/web/test/games/pool.determinism.test.ts` pins down.
 */
export function simulateShot(startBalls: readonly PoolBall[], shot: PoolShotInput): PoolSimulation {
  const world = new World({ gravity: Vec2(0, 0) });
  createCushions(world);

  // Creation order is `sortBalls` order (ascending number) so Planck's internal body list —
  // and therefore contact ordering inside the solver — is a function of the input alone.
  const ordered = sortBalls(startBalls);
  const bodies = new Map<number, Body>();
  for (const ball of ordered) bodies.set(ball.n, createBall(world, ball));

  let firstContact: number | null = null;
  const events: PoolSoundEvent[] = [];
  let steps = 0;
  world.on('begin-contact', (contact) => {
    const bodyA = contact.getFixtureA().getBody();
    const bodyB = contact.getFixtureB().getBody();
    const a = bodyA.getUserData();
    const b = bodyB.getUserData();

    // Sound bookkeeping: read-only (velocities are read, never set), so it can't perturb
    // the solver — the outcome is bit-identical with or without it.
    const va = bodyA.getLinearVelocity();
    const vb = bodyB.getLinearVelocity();
    const speed = Math.hypot(va.x - vb.x, va.y - vb.y);
    if (speed > 0.08) {
      events.push({
        atMs: (steps + 1) * POOL_PHYSICS_DT * 1000,
        kind: typeof a === 'number' && typeof b === 'number' ? 'ball' : 'cushion',
        speed,
      });
    }

    if (firstContact !== null) return;
    if (typeof a !== 'number' || typeof b !== 'number') return; // a cushion
    if (a === POOL_CUE_BALL && b !== POOL_CUE_BALL) firstContact = b;
    else if (b === POOL_CUE_BALL && a !== POOL_CUE_BALL) firstContact = a;
  });

  const cue = bodies.get(POOL_CUE_BALL);
  if (cue) {
    const power = Math.min(1, Math.max(0, shot.power));
    const speed = POOL_MIN_SHOT_SPEED + power * (POOL_MAX_SHOT_SPEED - POOL_MIN_SHOT_SPEED);
    cue.setLinearVelocity(Vec2(Math.cos(shot.angle) * speed, Math.sin(shot.angle) * speed));
  }

  const pocketed: number[] = [];
  const frames: PoolFrame[] = [snapshotBodies(bodies)];

  let settledAt = -1;
  for (; steps < MAX_STEPS; steps += 1) {
    world.step(POOL_PHYSICS_DT, POOL_VELOCITY_ITERATIONS, POOL_POSITION_ITERATIONS);

    // Pocket/escape sweep, in ascending ball number — so "the order they dropped" is
    // deterministic even when two balls drop on the same step.
    for (const [n, body] of [...bodies].sort((l, r) => l[0] - r[0])) {
      const p = body.getPosition();
      if (pocketHit(p.x, p.y) || escaped(p.x, p.y)) {
        pocketed.push(n);
        events.push({ atMs: (steps + 1) * POOL_PHYSICS_DT * 1000, kind: 'pocket', speed: 0 });
        bodies.delete(n);
        world.destroyBody(body);
      }
    }

    // Settle check: once everything is crawling, stop it dead. Exponential damping never
    // actually reaches zero, and "nearly stopped" is not a reproducible resting place.
    let maxSpeedSq = 0;
    for (const body of bodies.values()) {
      const v = body.getLinearVelocity();
      maxSpeedSq = Math.max(maxSpeedSq, v.x * v.x + v.y * v.y);
    }
    const settled = maxSpeedSq < STOP_SPEED * STOP_SPEED;
    if (settled) {
      for (const body of bodies.values()) {
        body.setLinearVelocity(Vec2(0, 0));
        body.setAngularVelocity(0);
      }
    }

    if ((steps + 1) % POOL_FRAME_STRIDE === 0 || settled) frames.push(snapshotBodies(bodies));
    if (settled) {
      settledAt = steps + 1;
      break;
    }
  }

  // Ran out of budget without settling — freeze it rather than leave the table in motion.
  if (settledAt < 0) {
    for (const body of bodies.values()) {
      body.setLinearVelocity(Vec2(0, 0));
      body.setAngularVelocity(0);
    }
    frames.push(snapshotBodies(bodies));
  } else {
    steps = settledAt;
  }
  const finalFrame = frames[frames.length - 1] as PoolFrame;

  const outcome: PoolShotOutcome = {
    balls: finalFrame.map((b) => ({ n: b.n, x: quantize(b.x), y: quantize(b.y) })),
    pocketed,
    firstContact,
  };
  return { outcome, frames, events, durationMs: steps * POOL_PHYSICS_DT * 1000 };
}

/**
 * Where the cue ball would go if it travelled unobstructed — the ray `aimPreview` builds the
 * guideline from. A cheap ray against the balls and cushions, never a simulation.
 */
export function aimGuide(
  balls: readonly PoolBall[],
  from: { x: number; y: number },
  angle: number,
): { x: number; y: number; hitBall: number | null } {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);

  // Distance to the cushion the ray would reach first.
  let best = Number.POSITIVE_INFINITY;
  const r = POOL_BALL_RADIUS;
  if (dx > 0) best = Math.min(best, (POOL_TABLE_LENGTH - r - from.x) / dx);
  if (dx < 0) best = Math.min(best, (r - from.x) / dx);
  if (dy > 0) best = Math.min(best, (POOL_TABLE_WIDTH - r - from.y) / dy);
  if (dy < 0) best = Math.min(best, (r - from.y) / dy);

  let hitBall: number | null = null;
  for (const ball of balls) {
    if (ball.n === POOL_CUE_BALL) continue;
    const ox = ball.x - from.x;
    const oy = ball.y - from.y;
    const along = ox * dx + oy * dy;
    if (along <= 0) continue;
    const perpSq = ox * ox + oy * oy - along * along;
    const reach = r * 2 * (r * 2) - perpSq;
    if (reach < 0) continue;
    const t = along - Math.sqrt(reach);
    if (t >= 0 && t < best) {
      best = t;
      hitBall = ball.n;
    }
  }

  const distance = Number.isFinite(best) ? Math.max(0, best) : 0;
  return { x: from.x + dx * distance, y: from.y + dy * distance, hitBall };
}

export interface PoolAimPreview {
  /** Where the cue ball's centre is when it first touches something (the ghost ball). */
  ghost: PoolPoint;
  /** The object ball it meets, or `null` for a cushion. */
  hitBall: number | null;
  /** Object ball: which way it heads off, and a length that grows with how full the hit is
   * (a thin cut sends it off slowly — a short line says so). Unit direction × length. */
  objectLine: PoolPoint | null;
  /** Cue ball after contact: the tangent line for a ball hit, the bounce for a cushion. */
  cueLine: PoolPoint | null;
}

/** How long the after-contact lines are at their fullest, in table units. Short on purpose:
 * enough to show the cut, not a full trajectory — you still judge the shot. */
const PREVIEW_LINE = 0.55;

/**
 * The aiming guideline, 8 Ball Pool style: a line to the ghost ball, then a short line for
 * where the object ball goes and one for where the cue ball deflects (the 90° tangent
 * line of a stun shot — we have no spin, so that's exactly what happens). Off a cushion it
 * shows one short bounce. Pure geometry; it never runs the simulator.
 */
export function aimPreview(
  balls: readonly PoolBall[],
  from: PoolPoint,
  angle: number,
): PoolAimPreview {
  const guide = aimGuide(balls, from, angle);
  const ghost = { x: guide.x, y: guide.y };
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);

  if (guide.hitBall !== null) {
    const ball = balls.find((b) => b.n === guide.hitBall);
    if (ball) {
      const ox = ball.x - ghost.x;
      const oy = ball.y - ghost.y;
      const len = Math.hypot(ox, oy) || 1;
      const ux = ox / len;
      const uy = oy / len;
      const full = Math.max(0, dx * ux + dy * uy); // cos of the cut angle
      const tx = dx - full * ux;
      const ty = dy - full * uy;
      // The tangent component's length is sin(cut): a full hit stops the cue ball dead.
      return {
        ghost,
        hitBall: guide.hitBall,
        objectLine: { x: ux * PREVIEW_LINE * full, y: uy * PREVIEW_LINE * full },
        cueLine: Math.hypot(tx, ty) > 1e-3 ? { x: tx * PREVIEW_LINE, y: ty * PREVIEW_LINE } : null,
      };
    }
  }

  // A cushion: reflect off whichever wall the ray reached.
  const r = POOL_BALL_RADIUS;
  const nearPocket = POOL_POCKETS.some(
    (p) => Math.hypot(p.x - ghost.x, p.y - ghost.y) < POOL_POCKET_RADIUS + r,
  );
  if (nearPocket) return { ghost, hitBall: null, objectLine: null, cueLine: null };
  const eps = 1e-6;
  let bx = dx;
  let by = dy;
  if (ghost.x <= r + eps || ghost.x >= POOL_TABLE_LENGTH - r - eps) bx = -bx;
  if (ghost.y <= r + eps || ghost.y >= POOL_TABLE_WIDTH - r - eps) by = -by;
  return {
    ghost,
    hitBall: null,
    objectLine: null,
    cueLine: { x: bx * PREVIEW_LINE * 0.6, y: by * PREVIEW_LINE * 0.6 },
  };
}
